import { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { useActivityLogs } from '../hooks/useActivityLogs';

export type UserRole = 'admin' | 'medico' | 'esteticista' | 'financeiro';

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  role: UserRole;
  avatar_url?: string;
  created_at: string;
}

const SUPER_ADMIN_EMAIL = 'tiago.seucorretor@gmail.com';

interface AuthContextType {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
  isSuperAdmin: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = profile?.email === SUPER_ADMIN_EMAIL;

  const buildFallbackProfile = (u: User): UserProfile => ({
    id: u.id,
    email: u.email || '',
    full_name: u.email?.split('@')[0] || 'Usuário',
    role: 'esteticista',
    created_at: new Date().toISOString(),
  });

  const loadProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email, full_name, role, avatar_url, created_at')
        .eq('id', userId)
        .maybeSingle();

      if (error || !data) {
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (currentUser) {
          setProfile(buildFallbackProfile(currentUser));
        }
        return;
      }

      setProfile(data as UserProfile);
    } catch {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        setProfile(buildFallbackProfile(currentUser));
      }
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      }
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const { logActivity } = useActivityLogs();

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        await loadProfile(data.user.id);

        // Registrar atividade de login
        await logActivity('Fez login no sistema', undefined, undefined, { email });
      }

      return { success: true };
    } catch (err: any) {
      console.error('Erro no login:', err);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, isSuperAdmin, signIn, login: signIn, signOut, logout: signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
